import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartContext, withCart } from "@insite/client-framework/Components/CartContext";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import SavedOrderDetailsCartLine from "@insite/content-library/Widgets/SavedOrderDetails/SavedOrderDetailsCartLine";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
/* Store Custom Code - Start */
import translate from "@insite/client-framework/Translate";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { useProductSettings } from "@store/Hooks/useSettings/useSettings";
import { COLORS, TYPOGRAPHY } from "@store/theme/common/design";
import { orderLinesHeadingStyles } from "@store/theme/common/mixins";
/* Store Custom Code - End */

type Props = WidgetProps & HasCartContext;

export interface SavedOrderDetailsProductListStyles {
    wrapper?: InjectableCss;
    cartLineCardContainer?: CardContainerStyles;
    // Store Custom Code
    cartLinesHeadingHidden?: HiddenProps;
    cartLinesHeading?: GridContainerProps;
    cartLinesHeadingInfoWrap?: GridItemProps;
    cartLinesHeadingProduct?: GridItemProps;
    cartLinesHeadingPrice?: GridItemProps;
    cartLinesHeadingQty?: GridItemProps;
    cartLinesHeadingSubtotal?: GridItemProps;
    cartLinesHeadingActions?: GridItemProps;
}

/* Store Custom Code - Start */
const cartLinesHeadingStyles = {
    css: css`
        ${orderLinesHeadingStyles()}
    `,
};
/* Store Custom Code - End */

export const savedOrderDetailsProductListStyles: SavedOrderDetailsProductListStyles = {
    wrapper: {
        css: css`
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    // Store Custom Code
    cartLinesHeadingHidden: {
        css: css`
            width: 100%;

            &.mobile-view {
                display: none !important;
            }
        `,
    },
    cartLinesHeading: {
        gap: 0,
        css: css`
            ${GridOffset} {
                min-height: 55px;
                background-color: ${COLORS.secondary.base};
            }
        `,
    },
    cartLinesHeadingProduct: {
        width: 6,
        ...cartLinesHeadingStyles,
    },
    cartLinesHeadingInfoWrap: {
        width: 6,
    },
    cartLinesHeadingPrice: {
        width: 3,
        ...cartLinesHeadingStyles,
    },
    cartLinesHeadingQty: {
        width: 2,
        ...cartLinesHeadingStyles,
    },
    cartLinesHeadingSubtotal: {
        width: 3,
        ...cartLinesHeadingStyles,
    },
    cartLinesHeadingActions: {
        width: 4,
        ...cartLinesHeadingStyles,
    },
};

const styles = savedOrderDetailsProductListStyles;

const SavedOrderDetailsProductList: React.FunctionComponent<Props> = ({ cart }) => {
    const { canSeePrices } = useProductSettings(); // Store Custom Code

    if (!cart || !cart.cartLines) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`savedOrderDetails_cartLinesList_${cart.id}`}>
            {/* Store Custom Code - Start */}
            <Hidden below="lg" {...styles.cartLinesHeadingHidden}>
                <GridContainer {...styles.cartLinesHeading}>
                    <GridItem {...styles.cartLinesHeadingProduct}>{translate("Products")}</GridItem>
                    <GridItem {...styles.cartLinesHeadingInfoWrap}>
                        <GridContainer {...styles.cartLinesHeading}>
                            <GridItem {...styles.cartLinesHeadingPrice} className="centered">
                                {canSeePrices ? translate("Price") : null}
                            </GridItem>
                            <GridItem {...styles.cartLinesHeadingQty} className="centered">
                                {translate("QTY")}
                            </GridItem>
                            <GridItem {...styles.cartLinesHeadingSubtotal} className="centered">
                                {canSeePrices ? translate("Subtotal") : null}
                            </GridItem>
                            <GridItem {...styles.cartLinesHeadingActions} className="centered">
                                {translate("Actions")}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </Hidden>
            {/* Store Custom Code - End */}
            {cart.cartLines.map(cartLine => (
                <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                    <SavedOrderDetailsCartLine cart={cart} />
                </CartLineContext.Provider>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withCart(SavedOrderDetailsProductList),
    definition: {
        allowedContexts: ["SavedOrderDetailsPage"],
        group: "Saved Order Details",
    },
};

export default widgetModule;
